/**
 * Alerts: the store's alert list with severity, acknowledge and clear, as a
 * popover off the HUD bell. The previous build recorded alerts but never
 * showed them.
 */
import { Popover } from 'radix-ui';
import { Bell, Check, Trash2, X } from 'lucide-react';
import { Chip, IconButton, type Tone } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useUI } from '@/state/store';
import { useTwin, type TwinAlert } from '../../store';
import { Button, EmptyState } from '../components/layout';
import { fmtTime } from '../lib/format';
import { useTwinUi } from '../state';

const SEVERITY_TONE: Record<TwinAlert['severity'], Tone> = { critical: 'warning', warning: 'caution', info: 'neutral' };

export function AlertsList({ onNavigate }: { onNavigate?: () => void }) {
  const alerts = useTwin((s) => s.alerts);
  const acknowledge = useTwin((s) => s.acknowledgeAlert);
  const clear = useTwin((s) => s.clearAlerts);
  const select = useUI((s) => s.select);
  const setReading = useTwinUi((s) => s.setReading);
  const setTab = useTwinUi((s) => s.setTab);
  const unacknowledged = alerts.filter((a) => !a.acknowledged).length;

  const goTo = (a: TwinAlert) => {
    if (!a.subsystemId) return;
    select({ subsystemId: a.subsystemId });
    if (a.dataId) {
      setReading({ subsystemId: a.subsystemId, dataId: a.dataId });
      setTab('readings');
    } else setTab('health');
    onNavigate?.();
  };

  return (
    <div className="flex flex-col gap-2 text-xs">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-ink-3">
          {alerts.length} alerts, {unacknowledged} open
        </span>
        <Button onClick={clear} disabled={alerts.length === 0} className="px-2 py-1">
          <Trash2 className="h-3 w-3" /> Clear
        </Button>
      </div>
      {alerts.length === 0 ? (
        <EmptyState title="No alerts.">Threshold crossings, failure stages and inventory checks appear here.</EmptyState>
      ) : (
        <ul className="flex max-h-[360px] flex-col gap-1 overflow-y-auto scrollbar-thin">
          {alerts.map((a) => (
            <li key={a.id} className={cn('rounded-control border px-2.5 py-2', a.acknowledged ? 'border-hairline opacity-60' : a.severity === 'critical' ? 'border-warning/40 bg-warning/5' : a.severity === 'warning' ? 'border-caution/40 bg-caution/5' : 'border-hairline')}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Chip tone={SEVERITY_TONE[a.severity]} className="px-1.5 py-0 text-[9px] uppercase">
                      {a.severity}
                    </Chip>
                    <span className="font-mono text-[10px] text-ink-3">{fmtTime(a.timeMs)}</span>
                  </div>
                  <button type="button" onClick={() => goTo(a)} disabled={!a.subsystemId} className="mt-0.5 text-left text-ink hover:text-accent disabled:cursor-default disabled:hover:text-ink">
                    {a.title}
                  </button>
                  {a.message && <div className="leading-snug text-ink-2">{a.message}</div>}
                  {a.subsystemId && (
                    <div className="font-mono text-[10px] text-ink-3">
                      {a.subsystemId}
                      {a.dataId ? `.${a.dataId}` : ''}
                    </div>
                  )}
                </div>
                {!a.acknowledged && (
                  <IconButton label="Acknowledge" className="h-6 w-6 shrink-0" onClick={() => acknowledge(a.id)}>
                    <Check className="h-3.5 w-3.5" />
                  </IconButton>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AlertsPopover({ className }: { className?: string }) {
  const alerts = useTwin((s) => s.alerts);
  const open = alerts.filter((a) => !a.acknowledged).length;
  const critical = alerts.some((a) => !a.acknowledged && a.severity === 'critical');
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className={cn('relative inline-flex h-8 w-8 items-center justify-center rounded-control border border-hairline-strong bg-raised/80 text-ink-2 transition hover:bg-overlay hover:text-ink', critical && 'border-warning/60 text-warning', className)} aria-label={`Alerts, ${open} open`} title="Alerts">
          <Bell className="h-4 w-4" />
          {open > 0 && <span className={cn('absolute -right-1 -top-1 min-w-4 rounded-full px-1 text-center font-mono text-[9px] font-semibold', critical ? 'bg-warning text-ink' : 'bg-caution text-accent-ink')}>{open}</span>}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 w-[380px] rounded-panel border border-hairline-strong bg-raised p-3 shadow-elev-2 focus:outline-none">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold text-ink">Alerts</div>
            <Popover.Close asChild>
              <IconButton label="Close" className="h-6 w-6">
                <X className="h-3.5 w-3.5" />
              </IconButton>
            </Popover.Close>
          </div>
          <AlertsList />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
